/**
 * All Control Panel endpoints live under the machine API base (which already
 * ends in /api), so every path here starts with /admin.
 */
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/admin/auth/login',
    REFRESH: '/admin/auth/refresh'
  },
  ACCOUNTS: {
    BASE: '/admin/accounts',
    BY_ID: (id: number | string) => `/admin/accounts/${id}`,
    FEATURE_GUARDS: (id: number | string) =>
      `/admin/accounts/${id}/feature-guards`,
    AI_INSIGHTS: (id: number | string) => `/admin/accounts/${id}/ai-insights`,
    PRODUCTS: (id: number | string) => `/admin/accounts/${id}/products`,
    PRODUCT: (id: number | string, product: string) =>
      `/admin/accounts/${id}/products/${product}`,
    ACTIVITY: (id: number | string) => `/admin/accounts/${id}/activity`,
    AUDIT_LOGS: (id: number | string) => `/admin/accounts/${id}/audit-logs`
  },
  BUSINESS_UNITS: {
    BASE: (accountId: number | string) =>
      `/admin/accounts/${accountId}/business-units`,
    BY_ID: (accountId: number | string, buId: number | string) =>
      `/admin/accounts/${accountId}/business-units/${buId}`
  },
  CLONE_PROD: {
    START: (accountId: number | string) => `/admin/accounts/${accountId}/clone-prod`,
    JOB: (accountId: number | string, jobId: string) =>
      `/admin/accounts/${accountId}/clone-prod/${jobId}`
  },
  ORDER_SAMPLES: {
    STORES: (accountId: number | string) => `/admin/accounts/${accountId}/order-samples/stores`,
    START: (accountId: number | string) => `/admin/accounts/${accountId}/order-samples`,
    JOB: (accountId: number | string, jobId: string) =>
      `/admin/accounts/${accountId}/order-samples/${jobId}`,
    FILE: (accountId: number | string, jobId: string) =>
      `/admin/accounts/${accountId}/order-samples/${jobId}/file`
  },
  PULSE_ONBOARDING: {
    START: (accountId: number | string) =>
      `/admin/accounts/${accountId}/pulse-onboarding`
  },
  INBOXES: {
    BASE: (accountId: number | string) => `/admin/accounts/${accountId}/inboxes`,
    BY_ID: (accountId: number | string, inboxId: number | string) =>
      `/admin/accounts/${accountId}/inboxes/${inboxId}`,
    /** Moves the inbox to a unit, or (DELETE) clears its primary. Existing, unchanged by sharing. */
    BUSINESS_UNIT: (accountId: number | string, inboxId: number | string) =>
      `/admin/accounts/${accountId}/inboxes/${inboxId}/business-unit`,
    /** Adds one unit to the inbox — becomes the primary if it had none yet. */
    BUSINESS_UNITS: (accountId: number | string, inboxId: number | string) =>
      `/admin/accounts/${accountId}/inboxes/${inboxId}/business-units`,
    /** Removes one unit. Removing the primary promotes the extra added first. */
    BUSINESS_UNITS_BY_ID: (
      accountId: number | string,
      inboxId: number | string,
      buId: number | string
    ) => `/admin/accounts/${accountId}/inboxes/${inboxId}/business-units/${buId}`,
    /** The unit must already be on the inbox. The old primary stays on it as an extra. */
    PRIMARY_BUSINESS_UNIT: (accountId: number | string, inboxId: number | string) =>
      `/admin/accounts/${accountId}/inboxes/${inboxId}/primary-business-unit`
  },
  CHANNEL_IDENTIFIERS: {
    /**
     * Pickable destinations, read live from each connected helpdesk. The only
     * channel-identifier endpoint left: a destination is written when its inbox is
     * created, so everything else is read back off the inbox itself.
     */
    AVAILABLE: (accountId: number | string) =>
      `/admin/accounts/${accountId}/channel-identifiers/available`
  },
  ACCOUNT_USERS: {
    BASE: (accountId: number | string) => `/admin/accounts/${accountId}/users`,
    BY_ID: (accountId: number | string, userId: number | string) =>
      `/admin/accounts/${accountId}/users/${userId}`
  },
  FEEDBACKS: {
    BASE: '/admin/feedback',
    REASONS: '/admin/feedback/reasons',
    ADDRESS: (id: string) => `/admin/feedback/${id}/address`,
    UNADDRESS: (id: string) => `/admin/feedback/${id}/unaddress`
  },
  ANALYTICS: {
    METRICS: '/admin/analytics/metrics',
    METRICS_SERIES: '/admin/analytics/metrics/series',
    CATEGORIES: '/admin/analytics/categories',
    FUNNEL: '/admin/analytics/funnel'
  }
} as const;
